import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Screen } from '../types';

interface AppContextType {
  currentScreen: Screen;
  setCurrentScreen: (screen: Screen) => void;
  showSplash: boolean;
  setShowSplash: (show: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [currentScreen, setCurrentScreen] = useState<Screen>('splash');
  const [showSplash, setShowSplash] = useState(true);

  // Check if user is already logged in and skip splash screen
  useEffect(() => {
    // Check for customer session first (temporary sessions take priority)
    const customerSession = localStorage.getItem('bookfood_customer_session');
    if (customerSession) {
      try {
        const customer = JSON.parse(customerSession);
        console.log('🚀 AppContext: Found customer session:', customer);
        setShowSplash(false);
        
        // Check if login is complete
        if (customer.loginComplete) {
          console.log('🚀 AppContext: Customer login complete, going to home');
          setCurrentScreen('customer-home');
        } else if (customer.orderType === 'dine-in') {
          console.log('🚀 AppContext: Customer needs table selection');
          setCurrentScreen('customer-table-selection');
        } else {
          console.log('🚀 AppContext: Customer login incomplete, going to login');
          setCurrentScreen('customer-login');
        }
        return;
      } catch (error) {
        console.error('❌ AppContext: Error parsing customer session:', error);
        localStorage.removeItem('bookfood_customer_session');
      }
    }
    
    // Check for admin user session
    const storedUser = localStorage.getItem('bookfood_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('🚀 AppContext: Found stored admin user, skipping splash screen');
        setShowSplash(false);
        setCurrentScreen(user.role === 'admin' ? 'admin-dashboard' : 'customer-home');
      } catch (error) {
        console.error('❌ AppContext: Error parsing stored user:', error);
        // Keep splash screen if there's an error
      }
    }
  }, []);

  const value = {
    currentScreen,
    setCurrentScreen,
    showSplash,
    setShowSplash
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
